import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { CrmService } from '../crm.service';
import { CrmProspect, CrmRoleOption, PageResponse, crmRoleLabel } from '../crm.model';
import { ToolbarService } from '../../services/toolbar.service';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MenuItem } from 'primeng/api';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';

@Component({
  selector: 'app-prospects-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    MultiSelectModule,
    SelectModule,
    TooltipModule,
    RippleModule,
    BreadcrumbModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './prospects-list.component.html',
  styleUrls: ['./prospects-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProspectsListComponent implements OnInit {
  private crmService = inject(CrmService);
  private toolbarService = inject(ToolbarService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private router = inject(Router);

  prospects = signal<CrmProspect[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  first = signal(0);
  rows = signal(10);
  searchText = signal('');
  statusFilter = signal('');
  sourceFilter = signal('');
  roleFilters = signal<string[]>([]);
  columnFilters = signal<Record<string, string>>({});
  crmRoles = signal<CrmRoleOption[]>([]);
  lastUpdated = signal<Date | null>(null);
  scorecards = signal<Record<string, any>>({});
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  breadcrumbItems: MenuItem[] = [
    { label: 'CRM', routerLink: '/crm/prospects' },
    { label: 'Prospects', routerLink: '/crm/prospects' }
  ];
  metrics = computed<PageMetric[]>(() => [
    { label: 'Total prospects', value: Number(this.scorecards()['total'] || this.totalRecords()) },
    { label: 'Active prospects', value: Number(this.scorecards()['active'] || 0), tone: 'info' },
    { label: 'B2B prospects', value: Number(this.scorecards()['b2b'] || 0) },
    { label: 'Converted', value: Number(this.scorecards()['converted'] || 0), tone: 'success' }
  ]);

  statuses = [
    { label: 'New', value: 'NEW' },
    { label: 'Qualified', value: 'QUALIFIED' },
    { label: 'Proposal', value: 'PROPOSAL' },
    { label: 'Lost', value: 'LOST' }
  ];

  ngOnInit() {
    this.loadRoles();
    this.loadScorecards();
    this.loadProspects();
  }

  loadRoles() {
    this.crmService.getCrmRoles().subscribe({
      next: roles => this.crmRoles.set(roles || []),
      error: () => this.crmRoles.set([])
    });
  }

  loadScorecards() {
    this.crmService.getProspectScorecards(this.toolbarService.customer()?.company?.id).subscribe(cards => this.scorecards.set(cards || {}));
  }

  loadProspects(event?: TableLazyLoadEvent) {
    if (event) {
      this.first.set(event.first ?? 0);
      this.rows.set(event.rows ?? 10);
    }
    const page = Math.floor(this.first() / this.rows());
    this.loading.set(true);
    this.crmService.searchProspects({
      companyId: this.toolbarService.customer()?.company?.id,
      searchText: this.searchText(),
      status: this.statusFilter(),
      source: this.sourceFilter(),
      companyName: this.columnFilters()['companyName'] || '',
      pocName: this.columnFilters()['pocName'] || '',
      pocEmail: this.columnFilters()['pocEmail'] || '',
      pocPhoneNumber: this.columnFilters()['pocPhoneNumber'] || '',
      prospectQuality: this.columnFilters()['prospectQuality'] || '',
      roles: this.roleFilters(),
      page,
      size: this.rows(),
      sortBy: 'createdAt',
      sortDir: 'desc'
    }).pipe(finalize(() => this.loading.set(false))).subscribe((response: PageResponse<CrmProspect>) => {
      this.prospects.set(response.content || []);
      this.totalRecords.set(response.totalElements || 0);
      this.lastUpdated.set(new Date());
      this.loadScorecards();
    });
  }

  applySearch() {
    this.first.set(0);
    this.loadProspects();
  }

  clearSearch() {
    this.searchText.set('');
    this.statusFilter.set('');
    this.sourceFilter.set('');
    this.roleFilters.set([]);
    this.columnFilters.set({});
    this.first.set(0);
    this.loadProspects();
  }

  setSearchText(value: string) {
    this.searchText.set(value || '');
  }

  setStatusFilter(value: string) {
    this.statusFilter.set(value || '');
  }

  setSourceFilter(value: string) {
    this.sourceFilter.set(value || '');
  }

  setRoleFilters(values: string[] | null | undefined) {
    this.roleFilters.set(values || []);
  }

  setColumnFilter(field: string, value: string) {
    this.columnFilters.update(filters => ({ ...filters, [field]: value || '' }));
  }

  openNew() {
    this.router.navigate(['/crm/prospects/new']);
  }

  editProspect(prospect: CrmProspect) {
    if (prospect.id) {
      this.router.navigate(['/crm/prospects', prospect.id, 'edit']);
    }
  }

  deleteProspect(prospect: CrmProspect) {
    if (!prospect.id) return;
    this.confirmationService.confirm({
      header: 'Confirm Deletion',
      message: `Delete prospect: <b>${prospect.companyName}</b>?`,
      accept: () => this.crmService.deleteProspect(prospect.id!).subscribe(() => {
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Prospect deleted' });
        this.loadProspects();
      })
    });
  }

  getSeverity(status: string) {
    switch (status) {
      case 'QUALIFIED': return 'success';
      case 'PROPOSAL': return 'warn';
      case 'LOST': return 'danger';
      default: return 'info';
    }
  }

  roleLabel(role?: string | null): string {
    return crmRoleLabel(role);
  }

  get activeSearchCount(): number {
    const filters = this.columnFilters();
    return [
      this.searchText(),
      this.statusFilter(),
      this.sourceFilter(),
      this.roleFilters().length ? this.roleFilters().join(',') : '',
      filters['companyName'],
      filters['pocName'],
      filters['pocEmail'],
      filters['pocPhoneNumber'],
      filters['prospectQuality']
    ].filter(value => !!String(value || '').trim()).length;
  }

  get openProspectCount(): number {
    return this.prospects().filter(prospect => !prospect.converted).length;
  }

  get convertedProspectCount(): number {
    return this.prospects().filter(prospect => prospect.converted).length;
  }

  get b2bProspectCount(): number {
    return this.prospects().filter(prospect => prospect.businessType === 'B2B').length;
  }

  get lastUpdatedText(): string {
    const updated = this.lastUpdated();
    return updated ? `Updated ${updated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '';
  }
}
